// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React, { useId } from 'react';

export interface PinIconProps {
  /** Square size in px (width and height). Defaults to 32. */
  size?: number;
  /** Brand colour applied to the outline (idle) or fill (pinned). Defaults to brand purple. */
  color?: string;
  /**
   * When true, the circle is filled and the thumbtack stroke flips to white —
   * the visual "pinned/active" state. Defaults to false (outlined).
   */
  pinned?: boolean;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
}

const THUMBTACK_PATH =
  'M10 22L13.0867 18.9127M13.09 18.9093L11.2367 17.056C10.6007 16.4207 11.2407 15.0587 12.11 15.004C12.8953 14.954 14.7133 15.2387 15.318 14.634L16.978 12.974C17.3893 12.562 17.128 11.6407 17.1013 11.1327C17.0627 10.4553 18.14 9.61866 18.7113 10.19L21.8093 13.2887C22.3827 13.8607 21.5427 14.9347 20.8673 14.8987C20.3593 14.872 19.4373 14.6107 19.0253 15.022L17.3653 16.682C16.7613 17.2867 17.0453 19.104 16.996 19.8893C16.9413 20.7593 15.5793 21.3993 14.9427 20.7627L13.09 18.9093Z';

/**
 * Round "pin / save for later" icon used on the Quote Options cards.
 *
 * <ul>
 *   <li>Idle ({@code pinned=false}): outlined circle stroked in {@code color}, thumbtack glyph also stroked in {@code color}.</li>
 *   <li>Pinned ({@code pinned=true}): solid filled circle in {@code color}, thumbtack glyph stroked in white.</li>
 * </ul>
 *
 * Designed to be embedded inside a transparent button.
 */
export const PinIcon: React.FC<PinIconProps> = ({
  size = 32,
  color = '#2D0A6C',
  pinned = false,
  className,
  style,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
}) => {
  const reactId = useId();
  // useId returns a string with colons that aren't valid in url(#...) refs in
  // some environments; sanitise it.
  const clipId = `pin-icon-clip-${reactId.replace(/:/g, '')}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden ?? !ariaLabel}
      data-testid="pin-icon"
      data-pinned={pinned}
    >
      {pinned ? (
        <rect width="32" height="32" rx="16" fill={color} />
      ) : (
        <rect x="0.5" y="0.5" width="31" height="31" rx="15.5" stroke={color} />
      )}
      <g clipPath={`url(#${clipId})`}>
        <path
          d={THUMBTACK_PATH}
          stroke={pinned ? '#FFFFFF' : color}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="16" height="16" fill="white" transform="translate(8 8)" />
        </clipPath>
      </defs>
    </svg>
  );
};
